import React, { useEffect, useState } from 'react';
import { type ProjectFollowUp, type ProjectPerson } from '../../services/selaApi';

type ModalShellProps = {
  title: string;
  description?: string;
  children: React.ReactNode;
  onClose: () => void;
};

function ModalShell({ title, description, children, onClose }: ModalShellProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl border border-[#d8e0eb] bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-[#0f172a]">{title}</h2>
            {description ? <p className="mt-1 text-sm leading-relaxed text-[#647488]">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm font-semibold text-[#647488] hover:bg-slate-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export type AssignRolePersonValues = {
  name: string;
  role: string;
  company: string;
  email: string;
};

export function AssignRolePersonModal({
  role,
  roleOptions = [],
  title,
  description,
  onSubmit,
  onClose,
  busy,
}: {
  role: string;
  roleOptions?: string[];
  title?: string;
  description?: string;
  onSubmit: (values: AssignRolePersonValues) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const uniqueRoleOptions = Array.from(new Set([role, ...roleOptions].map((item) => item.trim()).filter(Boolean)));
  const [selectedRole, setSelectedRole] = useState(uniqueRoleOptions[0] || '');
  const [customRole, setCustomRole] = useState('');
  const [values, setValues] = useState<Omit<AssignRolePersonValues, 'role'>>({ name: '', company: '', email: '' });
  const resolvedRole = selectedRole === '__custom__' ? customRole.trim() : selectedRole.trim();

  return (
    <ModalShell
      title={title || `Add ${role}`}
      description={description || 'Add the roster member who will own this role and its roadmap actions.'}
      onClose={onClose}
    >
      <form
        className="mt-5 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (values.name.trim() && resolvedRole) onSubmit({ ...values, role: resolvedRole });
        }}
      >
        <input
          value={values.name}
          onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
          placeholder="Name"
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm"
          autoFocus
        />
        <select
          value={selectedRole}
          onChange={(event) => setSelectedRole(event.target.value)}
          className="w-full rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-sm text-[#0f172a]"
        >
          {uniqueRoleOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
          <option value="__custom__">Add new role...</option>
        </select>
        {selectedRole === '__custom__' ? (
          <input
            value={customRole}
            onChange={(event) => setCustomRole(event.target.value)}
            placeholder="New role"
            className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm"
          />
        ) : null}
        <input
          value={values.company}
          onChange={(event) => setValues((prev) => ({ ...prev, company: event.target.value }))}
          placeholder="Company"
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm"
        />
        <input
          value={values.email}
          onChange={(event) => setValues((prev) => ({ ...prev, email: event.target.value }))}
          placeholder="Email"
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm"
        />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-[#cdd5e1] px-3 py-2 text-xs font-semibold text-[#475669]">
            Cancel
          </button>
          <button type="submit" disabled={busy || !values.name.trim() || !resolvedRole} className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
            Add roster member
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

export type OpenAskValues = {
  openAsk: string;
  dueDate: string;
};

export type LinkedOpenAskValues = OpenAskValues & {
  personId: string;
};

export type AssignRequirementActionValues = {
  personId: string;
  dueDate: string;
  notes: string;
};

export function AssignRequirementActionModal({
  title = 'Assign roster member',
  description,
  people,
  existingAction,
  onSubmit,
  onClose,
  busy,
}: {
  title?: string;
  description?: string;
  people: ProjectPerson[];
  existingAction?: ProjectFollowUp;
  onSubmit: (values: AssignRequirementActionValues) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const [values, setValues] = useState<AssignRequirementActionValues>({
    personId: existingAction?.personId || people[0]?.personId || '',
    dueDate: existingAction?.dueDate || '',
    notes: existingAction?.notes || '',
  });

  useEffect(() => {
    setValues({
      personId: existingAction?.personId || people[0]?.personId || '',
      dueDate: existingAction?.dueDate || '',
      notes: existingAction?.notes || '',
    });
  }, [existingAction, people]);

  const hasPeople = people.length > 0;

  return (
    <ModalShell title={title} description={description || 'Assign this Roadmap item to the roster member responsible for moving it forward.'} onClose={onClose}>
      {!hasPeople ? (
        <div className="mt-5 rounded-lg border border-dashed border-[#cdd5e1] bg-[#f8fafc] p-4 text-sm text-[#647488]">
          Add a roster member first. Roadmap items need a roster member before they can become assigned actions.
        </div>
      ) : null}
      <form
        className="mt-5 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (values.personId) onSubmit(values);
        }}
      >
        <label className="block text-xs font-semibold uppercase tracking-wide text-[#647488]">Roster member responsible</label>
        <select
          value={values.personId}
          disabled={!hasPeople}
          onChange={(event) => setValues((prev) => ({ ...prev, personId: event.target.value }))}
          className="w-full rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-sm"
        >
          {people.map((person) => (
            <option key={person.personId} value={person.personId}>
              {person.name}{person.roles?.[0] ? ` - ${person.roles[0]}` : ''}
            </option>
          ))}
        </select>
        <label className="block text-xs font-semibold uppercase tracking-wide text-[#647488]">Due date</label>
        <input
          value={values.dueDate}
          disabled={!hasPeople}
          onChange={(event) => setValues((prev) => ({ ...prev, dueDate: event.target.value }))}
          type="date"
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm disabled:bg-slate-50"
        />
        <label className="block text-xs font-semibold uppercase tracking-wide text-[#647488]">Note</label>
        <textarea
          value={values.notes}
          disabled={!hasPeople}
          onChange={(event) => setValues((prev) => ({ ...prev, notes: event.target.value }))}
          rows={3}
          placeholder="Optional context for the roster member"
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm disabled:bg-slate-50"
        />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-[#cdd5e1] px-3 py-2 text-xs font-semibold text-[#475669]">
            Cancel
          </button>
          <button type="submit" disabled={busy || !values.personId} className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
            Save assignment
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

export function OpenAskModal({
  title = 'Add action',
  description,
  defaultAsk = '',
  onSubmit,
  onClose,
  busy,
}: {
  title?: string;
  description?: string;
  defaultAsk?: string;
  onSubmit: (values: OpenAskValues) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const [values, setValues] = useState<OpenAskValues>({ openAsk: defaultAsk, dueDate: '' });

  useEffect(() => {
    setValues({ openAsk: defaultAsk, dueDate: '' });
  }, [defaultAsk]);

  return (
    <ModalShell title={title} description={description || 'Track a specific request, question, reminder, or document needed to resolve this item.'} onClose={onClose}>
      <form
        className="mt-5 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (values.openAsk.trim()) onSubmit(values);
        }}
      >
        <textarea
          value={values.openAsk}
          onChange={(event) => setValues((prev) => ({ ...prev, openAsk: event.target.value }))}
          placeholder="What do they owe?"
          rows={4}
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm"
          autoFocus
        />
        <input
          value={values.dueDate}
          onChange={(event) => setValues((prev) => ({ ...prev, dueDate: event.target.value }))}
          type="date"
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm"
        />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-[#cdd5e1] px-3 py-2 text-xs font-semibold text-[#475669]">
            Cancel
          </button>
          <button type="submit" disabled={busy || !values.openAsk.trim()} className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
            Save action
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

export function LinkedOpenAskModal({
  title = 'Add action',
  description,
  defaultAsk = '',
  people,
  onSubmit,
  onClose,
  busy,
}: {
  title?: string;
  description?: string;
  defaultAsk?: string;
  people: ProjectPerson[];
  onSubmit: (values: LinkedOpenAskValues) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const [values, setValues] = useState<LinkedOpenAskValues>({
    personId: people[0]?.personId || '',
    openAsk: defaultAsk,
    dueDate: '',
  });

  useEffect(() => {
    setValues({
      personId: people[0]?.personId || '',
      openAsk: defaultAsk,
      dueDate: '',
    });
  }, [defaultAsk, people]);

  const hasPeople = people.length > 0;

  return (
    <ModalShell title={title} description={description || 'Choose the roster member who owns this action, then track what they owe and when it is due.'} onClose={onClose}>
      {!hasPeople ? (
        <div className="mt-5 rounded-lg border border-dashed border-[#cdd5e1] bg-[#f8fafc] p-4 text-sm text-[#647488]">
          Add a roster member first. Roadmap actions need a responsible person so Sela can track who owns what.
        </div>
      ) : null}
      <form
        className="mt-5 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (values.personId && values.openAsk.trim()) onSubmit(values);
        }}
      >
        <label className="block text-xs font-semibold uppercase tracking-wide text-[#647488]">Roster member responsible</label>
        <select
          value={values.personId}
          disabled={!hasPeople}
          onChange={(event) => setValues((prev) => ({ ...prev, personId: event.target.value }))}
          className="w-full rounded-md border border-[#cdd5e1] bg-white px-3 py-2 text-sm"
        >
          {people.map((person) => (
            <option key={person.personId} value={person.personId}>
              {person.name}{person.roles?.[0] ? ` - ${person.roles[0]}` : ''}
            </option>
          ))}
        </select>
        <textarea
          value={values.openAsk}
          disabled={!hasPeople}
          onChange={(event) => setValues((prev) => ({ ...prev, openAsk: event.target.value }))}
          placeholder="What do they owe?"
          rows={4}
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm disabled:bg-slate-50"
          autoFocus={hasPeople}
        />
        <input
          value={values.dueDate}
          disabled={!hasPeople}
          onChange={(event) => setValues((prev) => ({ ...prev, dueDate: event.target.value }))}
          type="date"
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm disabled:bg-slate-50"
        />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-[#cdd5e1] px-3 py-2 text-xs font-semibold text-[#475669]">
            Cancel
          </button>
          <button type="submit" disabled={busy || !values.personId || !values.openAsk.trim()} className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
            Save action
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

export function TextEntryModal({
  title,
  description,
  label = 'Note',
  defaultValue = '',
  submitLabel = 'Save',
  onSubmit,
  onClose,
  busy,
}: {
  title: string;
  description?: string;
  label?: string;
  defaultValue?: string;
  submitLabel?: string;
  onSubmit: (value: string) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  return (
    <ModalShell title={title} description={description} onClose={onClose}>
      <form
        className="mt-5 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(value);
        }}
      >
        <label className="block text-xs font-semibold uppercase tracking-wide text-[#647488]">{label}</label>
        <textarea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          rows={4}
          className="w-full rounded-md border border-[#cdd5e1] px-3 py-2 text-sm"
          autoFocus
        />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-[#cdd5e1] px-3 py-2 text-xs font-semibold text-[#475669]">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
            {submitLabel}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
